// packages/frontend/src/features/shelves/ShelfRow.tsx
//
// One smart shelf on the library home (Plan 13, Task 8): a name header, a
// horizontally-scrollable row of RecipeCards at a fixed width, and a
// "See all" escape hatch. "See all" doesn't navigate anywhere — it asks
// Library.tsx (via `onSeeAll`) to scope the flat grid/list to this shelf's
// full (uncapped) membership through the `?shelf=<id>` URL param.
import type { JSX } from "react";
import type { RecipeApiItem, TaxonomyApiItem } from "@recipevault/shared";
import { RecipeCard } from "../recipes/RecipeCard.js";
import type { Shelf } from "./deriveShelves.js";

interface Props {
  shelf: Shelf;
  householdId: string;
  taxonomy: Map<string, TaxonomyApiItem>;
  /** Library.tsx sets `?shelf=<id>` (plus a mapped sort where one exists). */
  onSeeAll: (shelfId: string) => void;
}

export function ShelfRow({ shelf, householdId, taxonomy, onSeeAll }: Props): JSX.Element {
  return (
    <section aria-label={shelf.name} className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-zinc-200">{shelf.name}</h2>
        <button
          type="button"
          onClick={() => { onSeeAll(shelf.id); }}
          aria-label={`See all — ${shelf.name}`}
          className="inline-flex min-h-12 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium text-emerald-400 transition-colors hover:text-emerald-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
        >
          See all
          <span aria-hidden="true">→</span>
        </button>
      </div>
      {/* overflow-x-auto is scoped to this row only — the page body itself
          never scrolls horizontally. */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {shelf.recipes.map((r: RecipeApiItem) => (
          <div key={r.id} className="w-40 shrink-0">
            <RecipeCard recipe={r} householdId={householdId} taxonomy={taxonomy} />
          </div>
        ))}
      </div>
    </section>
  );
}
